import React, { ForwardRefRenderFunction, MouseEventHandler, forwardRef, useContext, useEffect } from 'react'

import filter from 'lodash/filter'
import first from 'lodash/first'
import isEmpty from 'lodash/isEmpty'

import { STYLES } from '@/constants'
import MapContext from '@/context/map'

import PathsContainer, { PathSVG } from '../paths-container'
import TextsContainer, { TextSVG } from '../text-container'

import { svgConstantsProps } from './constants'

export interface JSONMap {
    height?: string
    width?: string
    elements: Array<TextSVG | PathSVG>
}

interface MapTypes {
    [otherData: string]: Array<TextSVG | PathSVG>
}

export interface MapCallbacks {
    onClick?: MouseEventHandler<SVGSVGElement>
    onMouseOver?: MouseEventHandler<SVGSVGElement>
    onMouseLeave?: MouseEventHandler<SVGSVGElement>
    onPointerDownCapture?: MouseEventHandler<SVGSVGElement>
    onPointerUpCapture?: MouseEventHandler<SVGSVGElement>
    onDoubleClick?: MouseEventHandler<SVGSVGElement>
}

export interface SimpleMapProps {
    jsonMap?: JSONMap
    callbacks?: MapCallbacks
    disableTouchEventListeners?: boolean
    className?: string
}

const SimpleMap: ForwardRefRenderFunction<SVGSVGElement, SimpleMapProps> = ({ jsonMap, callbacks, className }, ref) => {
    const {
        selectors: { mapState },
    } = useContext(MapContext)

    useEffect(() => {
        const config = { attributes: true, subtree: true }
        const mapEl = typeof document !== 'undefined' ? document.querySelector('[class*=original-map]') : null

        const styleMutationCallback = (mutationList: any) => {
            /*
             * Look for mutations that have a sectionId and are hightlighted. Call the onMouseOver with the
             * retrived mutation OR a blank object that has no target.id (this prevent the tooltip from appearing on unmapped listings)
             */
            const mutations = filter(mutationList, (mutation) => {
                const { target } = mutation

                return (
                    mutation.type === 'attributes' &&
                    mutation.attributeName === 'style' &&
                    mutation.target.nodeName === 'path' &&
                    target.getAttribute('id') !== '' &&
                    target.getAttribute('style').includes(STYLES.selectedSection.fill)
                )
            })

            const mutation = isEmpty(mutations) ? { target: { id: '' } } : first(mutations)

            ;(callbacks?.onMouseOver || (() => {}))(mutation)
        }

        const observer = new MutationObserver(styleMutationCallback)

        if (mapEl) {
            observer.observe(mapEl, config)
        }

        return () => {
            observer.disconnect()
        }
    }, [])

    if (!jsonMap) return null
    const { elements, width, height } = jsonMap

    const mapByType =
        elements &&
        elements.reduce<MapTypes>((acc, item) => {
            const { type } = item

            if (acc[type]) acc[type].push(item)
            else acc[type] = [item]

            return acc
        }, {})

    return (
        <svg
            ref={ref}
            className={className}
            data-testid="svg-map"
            viewBox={`0 0 ${width} ${height}`}
            {...callbacks}
            {...svgConstantsProps}
            style={{ width: '100%', height: '100%', cursor: mapState.isMouseDown ? 'grabbing' : 'grab' }}
        >
            <PathsContainer paths={mapByType.path as Array<PathSVG>} />
            <TextsContainer texts={mapByType.text as Array<TextSVG>} />
        </svg>
    )
}

export default forwardRef(SimpleMap)
